import { observer } from 'mobx-react-lite'
import React, { useMemo } from 'react'
import { Box, CalendarDropdown, Label, Typography } from 'shared/ui'
import { SelectFieldMultiList } from 'shared/ui/Select/SelectMultiListField/SelectMultiListField'
import { Select } from 'shared/ui/Select/Select'
import { makeTextFieldIcon, TextFieldPlaceholder } from 'shared/ui/TextField'
import { ChipWithAvatar } from 'entities/Phone/ui/AssignNumberCommon/ui/ChipWithAvatar/ChipWithAvatar'
import { MultiSelectDropdownContent } from 'entities/Phone/ui/AssignNumberCommon/ui/NewInboxContent/MultiSelectDropdownContent'
import { type RequestAuditLogsStore } from 'pages/settings/pages/doubleOptIn/store/requestAuditLogsStore'
import { RequestAuditLogsEventTypesTrigger } from 'pages/settings/pages/doubleOptIn/ui/RequestAuditLogs/RequestAuditLogsEventTypesTrigger'
import styles from './styles.module.scss'

type IContentProps = {
  store: RequestAuditLogsStore
}

export const RequestAuditLogsModalContent = observer(({ store }: IContentProps) => {
  const {
    calendarFilterStore,
    members,
    selectedMembers,
    setSelectedMembers,
    deleteSelectedMembers,
    isSelectAllEvents,
    selectedEventTypes,
    eventTypeOptions,
    handleEventsTypeChange,
  } = store

  const membersWithEmail = useMemo(() => {
    return members.map((member) => ({
      ...member,
      labelContent: (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            alignItems: 'center',
            gap: '5px',
          }}
        >
          <Typography
            ellipsis
            variant='body-md-regular'
            color='var(--content-primary-primary)'
            ariaLabel={'Member name'}
          >
            {member.label}
          </Typography>
          {member.data?.email && (
            <Typography
              variant='body-xs-regular'
              color='var(--content-primary-tertiary)'
              ariaLabel={'Member email'}
            >
              {member.data.email}
            </Typography>
          )}
        </div>
      ),
    }))
  }, [members])

  const selectedMembersItems = useMemo(() => {
    return selectedMembers.map((item) => ({
      id: item.id,
      label: item.label,
      value: item.value,
      data: {
        isAway: undefined,
        avatarInfo: item.avatarProps?.info,
        email: item.data?.email,
      },
    }))
  }, [selectedMembers])

  return (
    <>
      <Box display={'grid'} gap={'16px'} paddingBottom={'8px'}>
        <Box>
          <Label
            label={{
              text: 'Date range',
            }}
            marginTop={-8}
            marginBottom={4}
          />
          <CalendarDropdown
            store={calendarFilterStore}
            triggerProps={{ size: 'medium', fullWidth: true }}
          />
        </Box>

        <Box>
          <SelectFieldMultiList
            withInitialSearch
            initialSearchPlaceholder={'Select members'}
            ChipComponent={ChipWithAvatar}
            labelText='Member'
            labelProps={{
              label: {
                text: 'Member',
                secondaryText: '(optional)',
              },
            }}
            dropdownProps={{
              textFieldProps: {
                className: styles.selectMembersSingleRowPadding,
              },
              PopperProps: {
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore
                      offset: ({ reference }) => {
                        return [-4, -reference.height - 8]
                      },
                    },
                  },
                ],
              },
            }}
            items={membersWithEmail}
            selectedItems={selectedMembersItems}
            onDelete={deleteSelectedMembers}
            onSelect={setSelectedMembers}
            fullWidth
            maxVisibleChips={1}
            customComponent={() => (
              <MultiSelectDropdownContent
                ChipComponent={ChipWithAvatar}
                items={membersWithEmail}
                selectedItems={selectedMembersItems}
                onSelect={setSelectedMembers}
                onDelete={deleteSelectedMembers}
                placeholder={'Search member'}
                withEmailSearch
              />
            )}
          />
        </Box>

        <Box>
          <Label label={{ text: 'Event type', secondaryText: '(optional)' }} marginBottom={4} />
          <Select
            text=''
            type='multi'
            values={selectedEventTypes}
            items={eventTypeOptions}
            dropdownProps={{
              ariaLabel: 'Select filter',
              width: 368,
              maxHeight: 220,
              onChange: (item) => {
                handleEventsTypeChange(item)
              },
              triggerComponent: () => (
                <TextFieldPlaceholder
                  variant={'stroke'}
                  multiline
                  className={styles.input}
                  placeholder={
                    <RequestAuditLogsEventTypesTrigger
                      selectedEventTypes={selectedEventTypes}
                      isSelectAllEvents={isSelectAllEvents}
                    />
                  }
                  rightActions={[makeTextFieldIcon({ icon: 'chevronDown' })]}
                />
              ),
              PopperProps: {
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore
                      offset: ({ reference }) => {
                        return [-4, -reference.height - 4]
                      },
                    },
                  },
                ],
              },
            }}
          />
        </Box>
      </Box>
    </>
  )
})
